import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import Brand from "@/components/Brand";
import PasswordInput from "@/components/PasswordInput";
import PasskeyLoginButton from "@/components/PasskeyLoginButton";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Briefcase, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

// Dedicated candidate login page. Rejects recruiter/admin roles with
// a friendly redirect prompt so the two login flows stay distinct.
export default function CandidateLogin() {
    const { user, setUser } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const redirectTo = params.get("redirect") || "/candidate";
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    // "Send me a login link" state
    const [magicSending, setMagicSending] = useState(false);
    const [magicSent, setMagicSent] = useState(false);

    // Same treatment as Login.jsx — when the user arrives here with an
    // active session, either fast-path to their portal (same role) or
    // sign them out so they can log in as a candidate cleanly.
    useEffect(() => {
        if (!user) return;
        if (user.role === "candidate") {
            nav(redirectTo, { replace: true });
            return;
        }
        api.post("/auth/logout").catch(() => {});
        setUser(false);
    }, [user, setUser, nav, redirectTo]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.role !== "candidate") {
                // Log them out on the client side by not persisting the recruiter session here.
                setErr(
                    "This login is for candidates only. Recruiters and admins should use the recruiter sign in page."
                );
                // Best-effort backend logout so their cookie isn't left dangling.
                try {
                    await api.post("/auth/logout");
                } catch (_) {
                    /* ignore */
                }
                return;
            }
            setUser(data);
            nav(redirectTo, { replace: true });
        } catch (ex) {
            setErr(describeAxiosError(ex));
        } finally {
            setLoading(false);
        }
    };

    // One-tap "Send login link" — mints (or reuses) a reusable magic-link
    // token on the server and emails it to `email`. Uniform response so we
    // don't leak whether the account exists.
    const sendLoginLink = async () => {
        const trimmed = (email || "").trim();
        if (!trimmed || !trimmed.includes("@")) {
            const msg = "Enter your email above first — we'll send the sign-in link there.";
            setErr(msg);
            toast.error(msg);
            return;
        }
        setErr("");
        setMagicSending(true);
        try {
            const { data } = await api.post("/auth/candidate/send-login-link", {
                email: trimmed,
            });
            setMagicSent(true);
            toast.success(data?.message || "Check your inbox for the sign-in link.");
            // Preview/dev: expose the URL for tester convenience.
            if (data?.dev_magic_url) {
                // eslint-disable-next-line no-console
                console.info("[dev] magic link:", data.dev_magic_url);
            }
        } catch (ex) {
            setErr(describeAxiosError(ex));
        } finally {
            setMagicSending(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex bg-paper" data-testid="candidate-login-page">
            <div className="w-full lg:w-2/3 flex items-center justify-center px-6 sm:px-12 py-12 bg-white border-r border-[#E5E5E5]">
                <div className="w-full max-w-md">
                    <div className="mb-12">
                        <Brand size="h-9" />
                    </div>

                    <div
                        data-testid="candidate-login-role-badge"
                        className="inline-flex items-center gap-2 mb-5 bg-cobalt text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em]"
                    >
                        <span className="inline-block w-1.5 h-1.5 bg-[#FDE047] rounded-full" />
                        Candidate portal · Sign in
                    </div>

                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
                        Welcome back, <br />
                        <span className="text-cobalt">jobseeker.</span>
                    </h1>
                    <p className="text-[#525252] mb-4 max-w-sm">
                        Track applications, check AI match scores, and earn referral bounties — all in one place.
                    </p>
                    <p className="text-xs text-[#A3A3A3] mb-10">
                        Are you a recruiter?{" "}
                        <Link
                            data-testid="switch-to-recruiter-login"
                            to="/login"
                            className="text-ink font-bold underline underline-offset-2 hover:text-cobalt"
                        >
                            Recruiter sign in →
                        </Link>
                    </p>

                    <form onSubmit={submit} className="space-y-4" data-testid="candidate-login-form">
                        {params.get("reason") && (
                            <div
                                data-testid="candidate-login-bounce-reason"
                                className="border border-cobalt/25 bg-cobalt/5 text-ink text-sm px-4 py-3 rounded"
                            >
                                {params.get("reason") === "idle" && "You were signed out after 60 minutes of inactivity. Please log in again."}
                                {params.get("reason") === "new_device" && "You signed in on another device. Please log in again to continue here."}
                                {!["idle", "new_device"].includes(params.get("reason")) && "Your session ended. Please log in again."}
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                Email
                            </label>
                            <input
                                data-testid="candidate-login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base text-ink placeholder-[#A3A3A3] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                Password
                            </label>
                            <PasswordInput
                                data-testid="candidate-login-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base text-ink placeholder-[#A3A3A3] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                            />
                        </div>

                        {err && (
                            <div
                                data-testid="candidate-login-error"
                                className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3"
                            >
                                {err}
                                {err.toLowerCase().includes("recruiter") && (
                                    <>
                                        {" "}
                                        <Link
                                            to="/login"
                                            className="underline underline-offset-2 font-bold"
                                        >
                                            Go to recruiter sign in →
                                        </Link>
                                    </>
                                )}
                            </div>
                        )}

                        <button
                            data-testid="candidate-login-submit-button"
                            disabled={loading}
                            type="submit"
                            className="btn-gradient w-full px-6 py-3.5 font-bold tracking-wide text-sm flex items-center justify-center gap-2"
                        >
                            {loading ? "Signing in…" : "Sign in as candidate"}{" "}
                            <ArrowRight size={16} weight="bold" />
                        </button>

                        {/* One-tap passwordless: email me a login link (V1.2 P2, Feb 2026) */}
                        <div className="pt-1">
                            <div className="flex items-center gap-3">
                                <div className="h-px flex-1 bg-[#E5E5E5]" />
                                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                                    or
                                </span>
                                <div className="h-px flex-1 bg-[#E5E5E5]" />
                            </div>
                            <button
                                type="button"
                                onClick={sendLoginLink}
                                disabled={magicSending || magicSent}
                                data-testid="candidate-send-login-link-btn"
                                className="mt-3 w-full border border-[#0A0A0A] text-[#0A0A0A] px-5 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 hover:bg-[#F5F5F5] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <EnvelopeSimple size={14} weight="bold" />
                                {magicSending
                                    ? "Sending link…"
                                    : magicSent
                                        ? "Link sent — check your inbox"
                                        : "Email me a sign-in link"}
                            </button>
                            <p
                                data-testid="candidate-send-login-link-help"
                                className="text-[11px] text-[#A3A3A3] mt-2 leading-snug"
                            >
                                No password to remember. We'll email you a one-tap
                                sign-in link valid for 30 days.
                            </p>

                            <div className="mt-4">
                                <PasskeyLoginButton mode="candidate" />
                            </div>
                        </div>

                        <div className="text-right">
                            <Link
                                to="/forgot-password"
                                data-testid="candidate-forgot-password-link"
                                className="text-xs font-bold text-cobalt hover:text-cobalt-dark underline underline-offset-2"
                            >
                                Forgot password?
                            </Link>
                        </div>
                    </form>

                    <p className="mt-8 text-sm text-[#525252]">
                        New here?{" "}
                        <Link
                            data-testid="goto-candidate-signup-from-login"
                            to={`/candidate/signup${redirectTo && redirectTo !== "/candidate" ? `?redirect=${encodeURIComponent(redirectTo)}` : ""}`}
                            className="text-cobalt font-bold underline underline-offset-4"
                        >
                            Create candidate account
                        </Link>
                    </p>
                    <p className="mt-2 text-xs text-[#A3A3A3]">
                        Are you a recruiter?{" "}
                        <Link
                            data-testid="goto-recruiter-login-from-candidate"
                            to="/login"
                            className="text-ink font-bold underline underline-offset-2"
                        >
                            Recruiter sign in
                        </Link>
                    </p>
                </div>
            </div>

            {/* Right: candidate-flavoured panel */}
            <div className="hidden lg:flex w-1/3 bg-ink text-white p-12 flex-col justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    Built for Indian jobseekers
                </p>
                <div>
                    <p className="font-heading font-black text-6xl xl:text-7xl leading-[0.95] tracking-tighter">
                        <span className="bg-[#FDE047] text-ink px-2 -mx-1 inline-block">
                            NaukriDaddy
                        </span>
                        ,<br />
                        <span className="text-cobalt">delivered.</span>
                    </p>
                    <p className="mt-8 text-white/60 max-w-xs flex items-center gap-2">
                        <Briefcase size={16} weight="bold" className="text-cobalt" />
                        One profile. Every shortlist. AI match on every resume.
                    </p>
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    © {new Date().getFullYear()} — NaukriDaddy.com
                </div>
            </div>
        </div>
    );
}
